import PageMeta from "components/PageMeta";
import { Tenant } from "models/Tenant";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Alert, Button, Card, CardBody, Col, FormGroup, Input, Label, Row, Spinner } from "reactstrap";
import * as api from "services/Api";
import { useOfficeTranslation } from "i18n/OfficeTranslation";

const ProjectCreate: React.FC = () => {
    const { t } = useOfficeTranslation();
    const navigate = useNavigate();
    const [tenants, setTenants] = useState<Tenant[]>([]);
    const [tenant, setTenant] = useState("");
    const [name, setName] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [budget, setBudget] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        api.getTenants()
            .then(tenantValues => {
                setTenants(tenantValues);
                if (tenantValues.length === 1) setTenant(tenantValues[0].id);
            })
            .catch(() => setError(t("projects.projectCreate.tenantsCouldNotBeLoaded")));
    }, [t]);

    const submit = async (event: React.FormEvent) => {
        event.preventDefault();
        setError("");
        if (!tenant || !name.trim() || !startDate || !endDate) {
            setError(t("projects.projectCreate.tenantNameStartDateAndEndDateAreRequired"));
            return;
        }
        if (new Date(endDate) < new Date(startDate)) {
            setError(t("projects.projectCreate.endDateMustBeOnOrAfterTheStartDate"));
            return;
        }
        const parsedBudget = Number(budget.replace(",", "."));
        if (!Number.isFinite(parsedBudget) || parsedBudget < 0) {
            setError(t("projects.projectCreate.enterAValidNonNegativeBudget"));
            return;
        }
        setSaving(true);
        try {
            const project = await api.createProject({
                tenant,
                name: name.trim(),
                start_date: new Date(`${startDate}T00:00:00`).toISOString(),
                end_date: new Date(`${endDate}T23:59:59`).toISOString(),
                budget: Math.round(parsedBudget * 100),
                currency: "EUR",
                workers: [],
                responsible: []
            });
            navigate(`/projects/${project.id}`);
        } catch (reason: any) {
            setError(reason.response?.data?.error ?? t("projects.projectCreate.projectCouldNotBeCreated"));
            setSaving(false);
        }
    };

    return <div className="page-content">
        <PageMeta title={t("common.newProject")} />
        <div className="page-title-box">
            <Link to="/projects" className="text-muted"><i className="mdi mdi-arrow-left me-1" />{t("common.projects")}</Link>
            <h4 className="mt-2 mb-0">{t("common.newProject")}</h4>
        </div>
        {error && <Alert color="danger">{error}</Alert>}
        <form onSubmit={submit}>
            <Row>
                <Col xl={8}>
                    <Card><CardBody>
                        <h5 className="mb-4">{t("projects.projectCreate.projectDetails")}</h5>
                        <FormGroup>
                            <Label for="project-name">{t("common.name")} *</Label>
                            <Input id="project-name" value={name} onChange={event => setName(event.target.value)}
                                placeholder={t("projects.projectCreate.forExampleOfficeRenovation")} autoFocus />
                        </FormGroup>
                        <FormGroup>
                            <Label for="project-tenant">{t("common.tenant")} *</Label>
                            <Input id="project-tenant" type="select" value={tenant} onChange={event => setTenant(event.target.value)}>
                                <option value="">{t("projects.projectCreate.chooseATenant")}</option>
                                {tenants.map(value => <option key={value.id} value={value.id}>{value.name}</option>)}
                            </Input>
                        </FormGroup>
                        <Row>
                            <Col md={6}><FormGroup><Label for="project-start">{t("projects.projectCreate.startDate")} *</Label>
                                <Input id="project-start" type="date" value={startDate} onChange={event => setStartDate(event.target.value)} />
                            </FormGroup></Col>
                            <Col md={6}><FormGroup><Label for="project-end">{t("projects.projectCreate.endDate")} *</Label>
                                <Input id="project-end" type="date" min={startDate} value={endDate} onChange={event => setEndDate(event.target.value)} />
                            </FormGroup></Col>
                        </Row>
                        <FormGroup>
                            <Label for="project-budget">{t("projects.projectCreate.budgetEur")}</Label>
                            <Input id="project-budget" type="number" min="0" step="0.01" value={budget}
                                onChange={event => setBudget(event.target.value)} placeholder="0.00" />
                            <small className="text-muted">{t("projects.projectCreate.enterTheAmountInEurosItIsStoredAccuratelyInCents")}</small>
                        </FormGroup>
                    </CardBody></Card>
                </Col>
            </Row>
            <div className="d-flex justify-content-end gap-2">
                <Link className="btn btn-light" to="/projects">{t("common.cancel")}</Link>
                <Button color="primary" type="submit" disabled={saving}>
                    {saving ? <><Spinner size="sm" className="me-2" />{t("projects.projectCreate.creating")}</> : t("projects.projectCreate.createProject")}
                </Button>
            </div>
        </form>
    </div>;
};

export default ProjectCreate;
